/**
 * Hash chain over the local log. Each event carries the hash of the previous one
 * from the same device, so a gap or an edit shows up. It is deliberately unkeyed:
 * a key would have to live on the same tablet as the data it protects, which
 * would prove nothing. This detects clumsy tampering and accidental loss, and
 * that is all it claims to do.
 */

const encoder = new TextEncoder()

/** Stable serialisation: object keys sorted, so the same event always hashes the same. */
export function canonicalise(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(canonicalise).join(',')}]`
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${JSON.stringify(k)}:${canonicalise(v)}`)
  return `{${entries.join(',')}}`
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

export async function sha256Hex(input: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', encoder.encode(input))
  return toHex(digest)
}

/** The hash an event contributes to the chain. */
export async function hashEvent(event: unknown): Promise<string> {
  return sha256Hex(canonicalise(event))
}
